import { Check, X } from 'lucide-react'
import type { Stage } from '@/lib/utils/status-stage'
import { cn } from '@/lib/utils'
import { STAGE_COLOR } from '@/lib/utils/status-stage'

interface StageGlyphProps {
  stage: Stage
  className?: string
}

/**
 * One shape per workflow stage (design 31a): the shape says where a status sits,
 * the name does the rest. Replaces "eight statuses, one blue circle". Stages and
 * colours live in lib/utils/status-stage.
 */
export function StageGlyph({ stage, className }: StageGlyphProps) {
  const color = STAGE_COLOR[stage]
  const base = cn('inline-grid size-3.5 flex-none place-items-center rounded-full box-border', className)

  if (stage === 'done' || stage === 'cancelled') {
    const Icon = stage === 'done' ? Check : X
    // text-white sits on the svg itself: menu items recolour any child svg without a
    // text-* class to muted-foreground, which beats the colour inherited from the span.
    return (
      <span aria-hidden className={base} style={{ background: color }}>
        <Icon className="size-[65%] text-white" strokeWidth={3} />
      </span>
    )
  }

  if (stage === 'backlog' || stage === 'planned') {
    return (
      <span
        aria-hidden
        className={base}
        style={{ border: `1.75px ${stage === 'backlog' ? 'dashed' : 'solid'} ${color}` }}
      />
    )
  }

  const fill = stage === 'active' ? '50%' : '75%'
  return (
    <span
      aria-hidden
      className={base}
      style={{
        border: `1.75px solid ${color}`,
        padding: '1.5px',
        backgroundClip: 'content-box',
        backgroundImage: `conic-gradient(${color} 0 ${fill}, transparent ${fill})`,
      }}
    />
  )
}
